import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useCallback,
  useImperativeHandle,
} from "react";
import "./DialogTable.css";

// SUBKOMPONENTE FÜR DEN INPUT-DIALOG
const AddDialog = ({ value, onChange, onOk, onCancel, title }) => ( 
  // Stoppt Propagation auf Overlay-Ebene (für Click Outside)
  <div className="dialog-table-add-dialog-overlay" onClick={(e) => e.stopPropagation()}> 
    {/* Stoppt Propagation auf Dialog-Ebene (für Click Outside) */}
    <div className="dialog-table-add-dialog" onClick={(e) => e.stopPropagation()}>
      <div className="dialog-table-add-dialog-content">
        <input
          type="text"
          value={value}
          onChange={onChange}
          placeholder={title || "Neuer Datensatzname"} 
          autoFocus 
          onKeyDown={(e) => {
            e.stopPropagation(); 
            if (e.key === 'Enter') {
              onOk();
            } else if (e.key === 'Escape') {
              onCancel();
            }
          }}
        />
      </div>
      <div className="dialog-table-add-dialog-actions">
        <button 
          className="dialog-table-add-dialog-btn dialog-table-add-dialog-btn-ok" 
          onClick={(e) => { // Sicherstellung der Propagation-Stopp bei Klick
            e.stopPropagation(); 
            onOk(); 
          }} 
          disabled={!value.trim()}
        >
          OK
        </button>
        <button 
          className="dialog-table-add-dialog-btn dialog-table-add-dialog-btn-cancel" 
          onClick={(e) => { // Sicherstellung der Propagation-Stopp bei Klick
             e.stopPropagation();
             onCancel();
          }}
        >
          Abbruch
        </button>
      </div>
    </div>
  </div>
);
// ENDE DER SUBKOMPONENTE


const DialogTable = forwardRef((props, ref) => {
  const { 
    id, 
    getRows, 
    showFilter, 
    filter: propFilter = "", 
    autoShow = "0", 
    onSelect, 
    onAfterRender,
    onCancel,
    onInsert,
    // NEUE PROP
    newRecordTitle // Titel für den Input-Platzhalter im Add-Dialog
  }
  = props;

  // Refs für Elemente und Callbacks
  const componentRef = useRef(null); 
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Stabile Referenzen für Props
  const onSelectRef = useRef(onSelect);
  const onAfterRenderRef = useRef(onAfterRender);
  const onCancelRef = useRef(onCancel);
  const onInsertRef = useRef(onInsert);

  useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);
  useEffect(() => { onAfterRenderRef.current = onAfterRender; }, [onAfterRender]);
  useEffect(() => { onCancelRef.current = onCancel; }, [onCancel]);
  useEffect(() => { onInsertRef.current = onInsert; }, [onInsert]);


  // Zustände
  const [filter, setFilter] = useState(showFilter === "1" ? "" : propFilter);
  const [rows, setRows] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [visible, setVisible] = useState(autoShow === "1");

  // Zustand für Add-Dialog
  const [isAdding, setIsAdding] = useState(false);
  const [newRecordValue, setNewRecordValue] = useState("");


  // Handler für den Add-Dialog
  const loadRows = useCallback(async () => {
    if (typeof getRows === "function") {
      try {
        const data = await getRows();
        setRows(Array.isArray(data) ? data : []);
      } catch {
        setRows([]);
      }
    } else {
      setRows([]);
    }
  }, [getRows]);

  const handleCancelAdd = useCallback(() => {
    setIsAdding(false);
    setNewRecordValue("");
  }, []); 

  const handleInsert = async () => {
    const value = newRecordValue.trim();
    if (!value) return;

    const insertFn = onInsertRef.current;
    if (typeof insertFn === "function") {
      // Verwenden Sie try/catch, um Exceptions abzufangen und zu verhindern, 
      // dass der Dialog bei Fehlern geschlossen wird.
      try {
        const success = await insertFn(value); 
        
        // ABBRUCH-PRÜFUNG: Wenn success === false zurückgegeben wird, hier beenden.
        // Der Dialog bleibt offen und der Wert erhalten.
        if (success === false) { 
          return; 
        }
        
        // --- ERFOLGS-LOGIK (NUR BEI ERFOLG WIRD HIER FORTGESETZT) ---
        
        // 1. Liste neu laden und Filter setzen
        await loadRows(); 
        setFilter(value); 
        
        // 2. Dialog schließen und Zustände zurücksetzen (NUR BEI ERFOLG!)
        setIsAdding(false);
        setNewRecordValue("");
        setHighlightedIndex(0); 

      } catch (e) {
        // Bei einer Exception bleibt der Dialog offen.
        console.error("Fehler beim Einfügen des Datensatzes:", e);
        return; 
      }
    }
  };

  const handleOpenAddDialog = (e) => {
    if(e) e.stopPropagation();
    setIsAdding(true);
  };
  

  // Hervorgehobene Zeile Ref
  const highlightedRowRef = useRef(null);
  useEffect(() => {
    const filteredSorted = rows
      .filter(r => r?.name?.toLowerCase().includes(filter.toLowerCase()))
      .sort((a, b) => a.name.localeCompare(b.name));
    highlightedRowRef.current = filteredSorted[highlightedIndex] || null;
  }, [rows, filter, highlightedIndex]);

  const scrollToItem = useCallback((index) => {
    if (listRef.current && listRef.current.children[index]) {
      listRef.current.children[index].scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, []);


  // **********************************
  // Effekte
  // **********************************

  // 1. onAfterRender Aufruf
  useEffect(() => {
    if (typeof onAfterRenderRef.current === "function") {
      onAfterRenderRef.current(visible);
    }
  }, [visible]);

  // 2. Click Outside Listener (schließt Dialog/Komponente)
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Wenn sichtbar, NICHT im Komponenten-Container und der Add-Dialog nicht offen ist
      if (visible && !isAdding && componentRef.current && !componentRef.current.contains(event.target)) {
        
        const cancelFn = onCancelRef.current;
        if (typeof cancelFn === "function") cancelFn();
        
        setVisible(false);
        
        // Propagation des Klicks stoppen, um zu verhindern, dass Eltern-Dialoge geschlossen werden
        if (event) event.stopPropagation();
      }
    };

    if (visible) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [visible, isAdding]);

  // 3. Globaler Keydown Listener (ESC, Enter, Pfeile)
  useEffect(() => {
    const handler = (e) => {
      if (!visible) return;
      
      // Priorität für den Add-Dialog
      if (isAdding) {
          if (e.key === "Escape") {
            handleCancelAdd();
            e.stopPropagation(); 
          }
          return;
      }

      // ESC (schließt Hauptkomponente)
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation(); 
        
        const cancelFn = onCancelRef.current;
        if (typeof cancelFn === "function") cancelFn();
        
        setVisible(false);
        return;
      }

      // Enter (Auswahl)
      if (e.key === "Enter") {
        const row = highlightedRowRef.current;
        if (row) {
          e.preventDefault();
          e.stopPropagation();
          const fn = onSelectRef.current;
          if (typeof fn === "function") fn({ ...row });
          setVisible(false);
        }
        return;
      }

      // Pfeile
      const displayedLength = rows.filter(r => r?.name?.toLowerCase().includes(filter.toLowerCase())).length;
      if (displayedLength > 0) {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
           e.preventDefault(); 
        }
        
        if (e.key === "ArrowDown") {
          if (highlightedIndex < displayedLength - 1) {
            const next = highlightedIndex + 1;
            setHighlightedIndex(next);
            scrollToItem(next);
          }
          return;
        }
        if (e.key === "ArrowUp") {
          if (highlightedIndex > 0) {
            const prev = highlightedIndex - 1;
            setHighlightedIndex(prev);
            scrollToItem(prev);
          }
          return;
        }
      }

      // Zeichen → Filter ergänzen
      if (
        showFilter === "1" &&
        e.key.length === 1 &&
        /^[a-zA-Z0-9 ]$/.test(e.key) &&
        document.activeElement !== inputRef.current
      ) {
        e.preventDefault();
        setFilter((prev) => prev + e.key);
        setHighlightedIndex(0);
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    };

    window.addEventListener("keydown", handler, { capture: true });
    return () => window.removeEventListener("keydown", handler, { capture: true });
  }, [visible, showFilter, highlightedIndex, rows, filter, scrollToItem, isAdding, handleCancelAdd]);

  // API für Elternkomponente
  useImperativeHandle(ref, () => ({
    show: async () => {
      await loadRows();
      setVisible(true);
      if (showFilter === "1") {
        setFilter("");
        setHighlightedIndex(0);
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    },
    hide: () => setVisible(false),
  }));

  // autoShow
  useEffect(() => {
    if (autoShow === "1") {
      (async () => {
        await loadRows();
        setVisible(true);
        if (showFilter === "1") {
          setTimeout(() => inputRef.current?.focus(), 0);
        }
      })();
    }
  }, [autoShow, loadRows, showFilter]);

  // Gefilterte & sortierte Rows für Rendering
  const displayedRows = rows
    .filter(r => r?.name?.toLowerCase().includes(filter.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  if (!visible) return null;

  return (
    <div id={id} className="dialog-table" ref={componentRef} onClick={(e) => e.stopPropagation()}>
      {showFilter === "1" && (
        <div className="dialog-table-filter-container">
          <input
            ref={inputRef}
            className="dialog-table-input"
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setHighlightedIndex(0);
            }}
          />
          {/* Plus Icon für Add-Funktionalität */}
          {typeof onInsertRef.current === "function" && (
             <i 
                className="bi bi-plus-square dialog-table-add-icon" 
                onClick={handleOpenAddDialog}
                title="Neuen Datensatz hinzufügen"
             ></i>
          )}
        </div>
      )}
      <ul className="dialog-table-list" ref={listRef}>
        {displayedRows.map((row, idx) => (
          <li
            key={row.id}
            className={idx === highlightedIndex ? "highlighted" : ""}
            onMouseEnter={() => setHighlightedIndex(idx)}
            onClick={(e) => {
              e.stopPropagation(); 
              const fn = onSelectRef.current;
              if (typeof fn === "function") fn({ ...row });
              setVisible(false);
            }}
          >
            {row.name}
          </li>
        ))}
      </ul>
      
      {/* Input Dialog */}
      {isAdding && (
        <AddDialog 
          value={newRecordValue}
          onChange={(e) => setNewRecordValue(e.target.value)}
          onOk={handleInsert}
          onCancel={handleCancelAdd}
          title={newRecordTitle} // NEUE PROP hier übergeben
        />
      )}
    </div>
  );
});

export default DialogTable;